// Shared UI for the Compliance v2 dashboards (Fuel + Toll). Consumes ONLY the
// live Compliance v2 contracts -- NO Shadow v2 / optimizer-comparison data here.
// Visual language matches the existing dispatcher pages (inline styles + CSS vars).
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Info, X } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// --------------------------------------------------------------------------- //
// Formatters
// --------------------------------------------------------------------------- //
export function fmtMoney(n, cents = false) {
  if (n == null || Number.isNaN(n)) return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
}
export function fmtPct(n, digits = 1) {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}
export function fmtGal(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `${Math.round(n).toLocaleString("en-US")} gal`;
}
export function fmtNum(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}
export function fmtPpg(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `$${n.toFixed(3)}`;
}
export function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}
export function shortId(id) {
  return id ? String(id).slice(0, 8) : "—";
}

// Variance is DESCRIPTIVE ("vs plan"), never "savings" -- we have no causal
// counterfactual. + = spent more than plan (red), - = spent less (green).
export function varianceSentence(n) {
  if (n == null) return "No comparable data";
  if (Math.abs(n) < 0.005) return "On plan";
  return n > 0 ? `${fmtMoney(n, true)} more than plan` : `${fmtMoney(Math.abs(n), true)} less than plan`;
}
export function varianceColor(n) {
  if (n == null || Math.abs(n) < 0.005) return "var(--muted)";
  return n > 0 ? "#dc2626" : "#16a34a";
}

// --------------------------------------------------------------------------- //
// InfoTip -- accessible hover/focus popover (no external dep)
// --------------------------------------------------------------------------- //
export function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={S.tipWrap}>
      <button
        type="button"
        aria-label={text}
        style={S.tipBtn}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <Info size={12} />
      </button>
      {open && <span role="tooltip" style={S.tipBody}>{text}</span>}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// KPI card + band
// --------------------------------------------------------------------------- //
export function Kpi({ label, value, sub, tone = "neutral", tip, valueColor }) {
  return (
    <div style={{ ...S.kpi, ...(TONE[tone] || {}) }}>
      <div style={S.kpiValue} title={typeof value === "string" ? value : undefined}>
        {valueColor ? <span style={{ color: valueColor }}>{value}</span> : value}
      </div>
      <div style={S.kpiLabel}>
        {label}
        {tip && <InfoTip text={tip} />}
      </div>
      {sub != null && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

export function Band({ title, note, children }) {
  return (
    <section style={S.band}>
      <div style={S.bandHead}>
        <span style={S.bandTitle}>{title}</span>
        {note && <span style={S.bandNote}>{note}</span>}
      </div>
      <div style={S.bandGrid}>{children}</div>
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Never-Drop identity check: company_actual == attributed + unattributed
// --------------------------------------------------------------------------- //
export function ReconcileBanner({ companyActual, attributed, unattributed }) {
  if (companyActual == null || attributed == null || unattributed == null) return null;
  const diff = companyActual - (attributed + unattributed);
  if (Math.abs(diff) <= 0.02) return null; // within currency rounding
  // Surface -- never hide -- a real reconciliation break.
  // eslint-disable-next-line no-console
  console.warn("[Compliance] Never-Drop identity broken:", { companyActual, attributed, unattributed, diff });
  return (
    <div style={S.reconcile}>
      Accounting check: Company Actual ({fmtMoney(companyActual, true)}) ≠ Attributed + Unattributed
      ({fmtMoney(attributed + unattributed, true)}) — off by {fmtMoney(diff, true)}. Reported for investigation.
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Methodology slide-over
// --------------------------------------------------------------------------- //
export function MethodologyButton({ onClick }) {
  return (
    <button type="button" style={S.methodBtn} onClick={onClick}>
      <Info size={13} /> How this is calculated
    </button>
  );
}

export function MethodologyDrawer({ open, onClose, title, items }) {
  if (!open) return null;
  return (
    <div style={S.drawerOverlay} onClick={onClose}>
      <div style={S.drawer} onClick={(e) => e.stopPropagation()}>
        <div style={S.drawerHead}>
          <span style={S.drawerTitle}>{title}</span>
          <button type="button" style={S.iconBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div style={S.drawerBody}>
          {items.map((it) => (
            <div key={it.term} style={S.defRow}>
              <div style={S.defTerm}>{it.term}</div>
              <div style={S.defText}>{it.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Transactions drilldown drawer -- consumes /fleet/{fuel|toll}-transactions.
// Filters (start/end/truck_id/attribution) mirror the cards so the table
// reconciles to the same population.
// --------------------------------------------------------------------------- //
export function TransactionsDrawer({ kind, start, end, truckId, attribution, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attr, setAttr] = useState(attribution || "all");

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = { start, end, limit: 500 };
    if (truckId) params.truck_id = truckId;
    if (attr && attr !== "all") params.attribution = attr;
    axios
      .get(`${apiBaseUrl}/scripted/fleet/${kind}-transactions`, { ...authHeaders(), params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [kind, start, end, truckId, attr]);

  const txns = data?.transactions || [];
  const totalCount = data?.count ?? 0;                 // total matching (pre-limit) per the API
  const displayed = txns.length;                        // rows actually returned (capped at limit)
  const truncated = totalCount > displayed;
  const displayedAmount = txns.reduce(
    (s, t) => s + Number(kind === "fuel" ? t.total_cost || 0 : t.price_usd || 0), 0);

  return (
    <div style={S.drawerOverlay} onClick={onClose}>
      <div style={{ ...S.drawer, width: "min(860px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
        <div style={S.drawerHead}>
          <span style={S.drawerTitle}>{kind === "fuel" ? "Fuel" : "Toll"} transactions</span>
          <button type="button" style={S.iconBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div style={S.txnFilters}>
          {["all", "attributed", "unattributed"].map((a) => (
            <button key={a} type="button"
              style={{ ...S.filterChip, ...(attr === a ? S.filterChipActive : {}) }}
              onClick={() => setAttr(a)}>
              {a[0].toUpperCase() + a.slice(1)}
            </button>
          ))}
          <span style={S.txnCount}>
            {loading ? "…" : `Displayed ${fmtNum(displayed)}${truncated ? ` of ${fmtNum(totalCount)}` : ""} · displayed amount ${fmtMoney(displayedAmount, true)}`}
          </span>
        </div>
        <div style={S.drawerBody}>
          {loading && <div style={S.txnMuted}>Loading transactions…</div>}
          {error && <div style={S.errorBox}>Couldn't load transactions: {error}</div>}
          {!loading && !error && txns.length === 0 && (
            <div style={S.txnMuted}>No {kind} transactions for this selection.</div>
          )}
          {!loading && !error && txns.length > 0 && (
            <div style={S.txnTableWrap}>
              <table style={S.txnTable}>
                <thead>
                  <tr>
                    <th style={S.th}>When</th>
                    <th style={S.th}>Truck</th>
                    <th style={S.th}>{kind === "fuel" ? "Station" : "Toll / plaza"}</th>
                    {kind === "fuel" && <th style={S.thR}>Gallons</th>}
                    {kind === "fuel" && <th style={S.thR}>$/gal</th>}
                    <th style={S.thR}>Amount</th>
                    <th style={S.th}>Load</th>
                    <th style={S.th}>Attribution</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => {
                    const unattr = !t.load_id;
                    return (
                      <tr key={t.id} style={unattr ? S.trUnattr : undefined}>
                        <td style={S.td}>{fmtDateTime(kind === "fuel" ? t.ended_at : t.recorded_at)}</td>
                        <td style={S.td}>#{t.unit_number || "?"}</td>
                        <td style={S.td}>{(kind === "fuel" ? t.location : t.toll_name) || <span style={S.italmuted}>Unrecognized</span>}</td>
                        {kind === "fuel" && <td style={S.tdR}>{t.gallons != null ? `${Math.round(t.gallons)}` : "—"}</td>}
                        {kind === "fuel" && <td style={S.tdR}>{t.price_per_gallon != null ? `$${Number(t.price_per_gallon).toFixed(2)}` : "—"}</td>}
                        <td style={S.tdR}>{fmtMoney(Number(kind === "fuel" ? t.total_cost : t.price_usd), true)}</td>
                        <td style={S.td}>{t.load_id ? shortId(t.load_id) : <AttrChip status={t.attribution_status} reason={t.reason} />}</td>
                        <td style={S.td}>
                          {t.load_id
                            ? <span style={{ ...S.chip, ...S.chipGood }}>attributed</span>
                            : <AttrChip status={t.attribution_status} reason={t.reason} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={S.txnFootnote}>
            {truncated && (
              <div style={{ color: "#d97706", marginBottom: 6 }}>
                Showing the first {fmtNum(displayed)} of {fmtNum(totalCount)} matching transactions — the
                displayed amount is a partial subtotal, not a period total.
              </div>
            )}
            Displayed amount is the sum of the rows shown for the current attribution filter — it is <b>not</b> Company
            Actual (which spans the full period and both attribution states). Unattributed = real company spend not
            yet safely assignable to a load (deadhead / forgotten pickup / conflict); it is included in Company Actual
            and is <b>not</b> an operational violation.
          </div>
        </div>
      </div>
    </div>
  );
}

function AttrChip({ status, reason }) {
  return (
    <span style={{ ...S.chip, ...S.chipAmber }} title={reason || status || "unattributed"}>
      {status === "unresolved_conflict" ? "held (conflict)" : "unattributed"}
    </span>
  );
}

const TONE = {
  neutral: {},
  primary: { borderColor: "rgba(37,99,235,0.28)", background: "linear-gradient(155deg, rgba(37,99,235,0.06), var(--card) 62%)" },
  amber: { borderColor: "rgba(217,119,6,0.32)", background: "linear-gradient(155deg, rgba(217,119,6,0.07), var(--card) 62%)" },
  good: { borderColor: "rgba(22,163,74,0.28)" },
};

const S = {
  tipWrap: { position: "relative", display: "inline-flex", marginLeft: 5, verticalAlign: "middle" },
  tipBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, width: 15, height: 15, border: "none", background: "none", color: "var(--muted)", cursor: "help" },
  tipBody: {
    position: "absolute", bottom: "140%", left: "50%", transform: "translateX(-50%)", zIndex: 40,
    width: 240, background: "var(--ink)", color: "var(--card)", fontSize: 11.5, lineHeight: 1.45,
    padding: "8px 10px", borderRadius: 8, boxShadow: "var(--shadow-md)", fontWeight: 500,
  },
  kpi: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "14px 16px", boxShadow: "var(--shadow-xs)", minWidth: 0 },
  kpiValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  kpiLabel: { fontSize: 12, color: "var(--muted)", marginTop: 3, display: "flex", alignItems: "center" },
  kpiSub: { fontSize: 11, color: "var(--muted)", marginTop: 4, fontVariantNumeric: "tabular-nums" },

  band: { marginBottom: 16 },
  bandHead: { display: "flex", alignItems: "baseline", gap: 10, margin: "0 2px 8px" },
  bandTitle: { fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--muted)" },
  bandNote: { fontSize: 11.5, color: "var(--muted)" },
  bandGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 },

  reconcile: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#dc2626", borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: 12.5, marginBottom: 14 },

  methodBtn: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap" },

  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.38)", zIndex: 60, display: "flex", justifyContent: "flex-end" },
  drawer: { width: "min(560px, 96vw)", height: "100%", background: "var(--card)", borderLeft: "1px solid var(--line)", boxShadow: "var(--shadow-lg)", display: "flex", flexDirection: "column" },
  drawerHead: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--line)" },
  drawerTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  iconBtn: { display: "inline-flex", padding: 6, border: "none", background: "none", color: "var(--muted)", cursor: "pointer", borderRadius: 6 },
  drawerBody: { padding: "14px 18px", overflowY: "auto", flex: 1 },
  defRow: { marginBottom: 12 },
  defTerm: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)" },
  defText: { fontSize: 12, color: "var(--muted)", marginTop: 2, lineHeight: 1.5 },

  txnFilters: { display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderBottom: "1px solid var(--line)", flexWrap: "wrap" },
  filterChip: { fontSize: 11.5, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 999, padding: "5px 12px", cursor: "pointer" },
  filterChipActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },
  txnCount: { marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "var(--muted)", fontVariantNumeric: "tabular-nums" },

  txnTableWrap: { overflowX: "auto" },
  txnTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "var(--muted)", padding: "6px 8px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  thR: { textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "var(--muted)", padding: "6px 8px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" },
  td: { padding: "7px 8px", borderBottom: "1px solid var(--line)", color: "var(--ink-2)", whiteSpace: "nowrap" },
  tdR: { padding: "7px 8px", borderBottom: "1px solid var(--line)", color: "var(--ink-2)", textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  trUnattr: { background: "rgba(217,119,6,0.05)" },
  italmuted: { color: "var(--muted)", fontStyle: "italic" },
  txnMuted: { fontSize: 12.5, color: "var(--muted)", padding: "20px 4px", textAlign: "center" },
  txnFootnote: { fontSize: 11, color: "var(--muted)", marginTop: 12, lineHeight: 1.5, borderTop: "1px solid var(--line)", paddingTop: 10 },

  chip: { fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" },
  chipGood: { background: "rgba(22,163,74,0.1)", color: "#16a34a" },
  chipAmber: { background: "rgba(217,119,6,0.12)", color: "#d97706" },
  errorBox: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626", borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13 },
};
